import { createApi } from "@reduxjs/toolkit/query/react";
import {
  AuthResponse,
  RegisterWithWalletRequest,
  RegisterWithWalletResponse,
  WalletBalanceResponse,
  WalletHistoryResponse,
  ConvertCryptoRequest,
  ConvertCryptoResponse,
  SwapRequest,
  SwapResponse,
  VerifiedAddress,
  AddVerifiedAddressRequest,
} from "./walletApiData";
import { postApi } from "@/store/api/postApi";
import { baseQueryWithReauth } from "./baseQueryWithReauth";
import { TimeRangeValue } from "@/types/timeRanges";
import { updatePost } from "@/store/slice/updatedPostsSlice";

export const walletApi = createApi({
  reducerPath: "walletApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["WalletBalance", "WalletStatus", "WalletHistory", "VerifiedAddresses"],
  endpoints: (builder) => ({
    //Registration
    checkEmail: builder.mutation<{ exists: boolean }, { email: string }>({
      query: ({ email }) => ({
        url: "/wallet-auth/check-email",
        method: "POST",
        body: { email },
      }),
    }),
    login: builder.mutation<AuthResponse, { email: string; password: string }>({
      query: ({ email, password }) => ({
        url: "/wallet-auth/login",
        method: "POST",
        body: { email, password },
      }),
    }),
    loginWithGoogle: builder.mutation<AuthResponse, { oauthToken: string }>({
      query: ({ oauthToken }) => ({
        url: "/wallet-auth/oauth/google",
        method: "POST",
        body: { oauthToken },
      }),
    }),

    sendFunds: builder.mutation<
      {
        success: boolean;
        transactionId: string;
        message: string;
        error?: string;
      },
      {
        recipientId: string;
        amount: number;
      }
    >({
      query: (body) => ({
        url: "/api/wallet/send-funds",
        method: "POST",
        body,
      }),
      invalidatesTags: ["WalletBalance", "WalletStatus"],
    }),
    loginWithApple: builder.mutation<AuthResponse, { oauthToken: string }>({
      query: ({ oauthToken }) => ({
        url: "/wallet-auth/oauth/apple",
        method: "POST",
        body: { oauthToken },
      }),
    }),
    getWalletStatus: builder.query<
      { connected: boolean; balance?: number; lastLogin?: string },
      void
    >({
      query: () => ({
        url: "/wallet-auth/status",
        method: "GET",
      }),
      providesTags: ["WalletStatus"],
    }),
    logoutFromWallet: builder.mutation<{ message: string }, void>({
      query: () => ({
        url: "/wallet-auth/logout",
        method: "POST",
      }),
    }),
    getWallets: builder.query<
      {
        accountId: string;
        isActive: boolean;
        balance: number;
        switchToken: string;
      }[],
      void
    >({
      query: () => ({
        url: "/wallet-auth/wallets",
        method: "GET",
      }),
    }),
    switchWallet: builder.mutation<
      {
        message: string;
        wallet: {
          uid: string;
          accountId: string;
          balance: number;
        };
      },
      { switchToken: string }
    >({
      query: ({ switchToken }) => ({
        url: "/wallet-auth/switch-wallet",
        method: "POST",
        body: { switchToken },
      }),
      invalidatesTags: ["WalletStatus", "WalletBalance"],
    }),

    getDepositLimits: builder.query<
      {
        monthlyLimit: number;
        depositedThisMonth: number;
        remainingAmount: number;
      },
      void
    >({
      query: () => ({
        url: "/api/wallet/deposit-limits",
        method: "GET",
      }),
      providesTags: ["WalletBalance"],
    }),

    //Wallet Forgot password
    passwordResetRequest: builder.mutation<
      { status: "success" | "error"; message: string },
      { email: string }
    >({
      query: (body) => ({
        url: "/auth/forgot-password",
        method: "POST",
        body,
      }),
    }),
    passwordResetResend: builder.mutation<
      { status: "success" | "error"; message: string },
      { email: string }
    >({
      query: (body) => ({
        url: "/auth/forgot-password",
        method: "POST",
        body,
      }),
    }),
    passwordResetConfirm: builder.mutation<
      {
        message: any;
        status: "success" | "error";
      },
      { token: string; password: string; passwordconf: string }
    >({
      query: (body) => ({
        url: "/auth/reset-password",
        method: "POST",
        body,
      }),
    }),

    //Wallet view/ Wallet History
    getWalletBalance: builder.query<WalletBalanceResponse, void>({
      query: () => ({
        url: "/api/wallet/balance",
      }),
      providesTags: ["WalletBalance"],
    }),
    getWalletHistory: builder.query<
      WalletHistoryResponse,
      {
        page?: number;
        limit?: number;
        time_range?: TimeRangeValue;
        type?: ("deposit" | "withdrawal" | "like" | "zap")[];
        order?: "asc" | "desc";
      }
    >({
      query: ({ page = 1, limit = 50, time_range = "", type, order = "desc" } = {}) => ({
        url: "/api/payments/transaction-history",
        params: { page, limit, time_range, type, order },
      }),
      providesTags: ["WalletHistory"],
    }),
    createDepositInvoice: builder.mutation<
      {
        invoice: string;
        qr_code: string;
        amount: number;
        note: string;
        expires_at: string;
      },
      {
        amount: number;
        // note: string;
        // speed_fee: number;
        // currency: "USDT" | "SATS" | "BTC";
      }
    >({
      query: (body) => ({
        url: "api/wallet/invoice",
        method: "POST",
        body,
      }),
    }),
    registerWithWallet: builder.mutation<RegisterWithWalletResponse, RegisterWithWalletRequest>({
      query: (body) => ({
        url: "/speed/wallet/register",
        method: "POST",
        body,
      }),
    }),
    buyWithUsdt: builder.mutation<
      {
        confirmation: string;
        newBalances: any;
        transaction: any;
      },
      {
        amount_usdt: number;
        target_currency: "BTC" | "SATS";
      }
    >({
      query: (body) => ({
        url: "/wallet/buy/usdt",
        method: "POST",
        body,
      }),
      invalidatesTags: ["WalletBalance"],
    }),

    //Wallet: SATS Top-Up & Send Flow
    getQrInvoice: builder.mutation<
      {
        invoice: string;
        qr_code: string;
        amount: number;
        note: string;
        expires_at: string;
      },
      {
        amount: number;
        note: string;
        speed_fee: number;
        currency: "SATS" | "BTC";
      }
    >({
      query: (body) => ({
        url: "api/wallet/invoice",
        method: "POST",
        body,
      }),
    }),
    sendSats: builder.mutation<
      {
        success: boolean;
        transaction_id: string;
        message: string;
        error?: string;
      },
      {
        invoice: string;
        metadata: {
          source: string;
          userId: string | number;
        };
      }
    >({
      query: (body) => ({
        url: "api/wallet/send",
        method: "POST",
        body,
      }),
      invalidatesTags: ["WalletBalance"],
    }),
    decodeQr: builder.mutation<{ invoice: string; valid: boolean; type: string }, FormData>({
      query: (formData) => ({
        url: "api/wallet/qr/decode",
        method: "POST",
        body: formData,
      }),
    }),
    initiateWithdrawal: builder.mutation<
      { sessionId: string; message: string },
      { amount: number; payoutAddress: string }
    >({
      query: (body) => ({
        url: "/api/withdrawals/initiate",
        method: "POST",
        body,
      }),
    }),
    resendWithdrawalOtp: builder.mutation<{ message: string }, { sessionId: string }>({
      query: (body) => ({
        url: "/api/withdrawals/resend-otp",
        method: "POST",
        body,
      }),
    }),
    confirmWithdrawal: builder.mutation<
      { success: boolean; message: string; transactionId?: string },
      { sessionId: string; otpCode: string }
    >({
      query: (body) => ({
        url: "/api/withdrawals/confirm",
        method: "POST",
        body,
      }),
      invalidatesTags: ["WalletBalance", "WalletHistory"],
    }),
    getVerifiedAddresses: builder.query<VerifiedAddress[], void>({
      query: () => ({
        url: "/api/withdrawals/verified-addresses",
        method: "GET",
      }),
      transformResponse: (response: { data: VerifiedAddress[] }) => response?.data ?? [],
      providesTags: ["VerifiedAddresses"],
    }),
    addVerifiedAddress: builder.mutation<VerifiedAddress, AddVerifiedAddressRequest>({
      query: (body) => ({
        url: "/api/withdrawals/verified-addresses",
        method: "POST",
        body,
      }),
      invalidatesTags: ["VerifiedAddresses"],
    }),
    deleteVerifiedAddress: builder.mutation<void, string>({
      query: (uid) => ({
        url: `/api/withdrawals/verified-addresses/${uid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["VerifiedAddresses"],
    }),
    // Platform Commission
    zap: builder.mutation<
      {
        success: boolean;
        zapId: string;
        transactionId: string;
        netAmount: number;
        message: string;
      },
      { post_id: string; amount_usdt: number; message?: string }
    >({
      query: (body) => ({
        url: "wallet/zap",
        method: "POST",
        body,
      }),
      async onQueryStarted({ post_id }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          const postData = await dispatch(
            postApi.endpoints.getPostByUid.initiate(post_id, { forceRefetch: true })
          ).unwrap();
          dispatch(updatePost(postData));
        } catch {}
      },
    }),

    like: builder.mutation<
      {
        success: boolean;
        message?: string;
        status?: "pending" | "succeeded" | "failed";
        transactionId?: string;
      },
      { post_id: string }
    >({
      query: (body) => ({
        url: "/wallet/like",
        method: "POST",
        body,
      }),
      invalidatesTags: ["WalletBalance"],
      async onQueryStarted({ post_id }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          const postData = await dispatch(
            postApi.endpoints.getPostByUid.initiate(post_id, { forceRefetch: true })
          ).unwrap();
          dispatch(updatePost(postData));
        } catch {}
      },
    }),
    purchaseFocusList: builder.mutation<
      { success: boolean; message?: string },
      { focus_list_id: string; amount_usdt: number }
    >({
      query: (body) => ({
        url: "/wallet/focus-list/purchase",
        method: "POST",
        body,
      }),
    }),
    //
    convertCrypto: builder.query<ConvertCryptoResponse, ConvertCryptoRequest>({
      query: ({ from, to = "USDC", amount }) => ({
        url: "/finance/crypto/convert",
        method: "GET",
        params: { from, to, amount },
      }),
    }),
    //
    swap: builder.mutation<SwapResponse, SwapRequest>({
      query: (body) => ({
        url: "/api/wallet/swap",
        method: "POST",
        body,
      }),
      invalidatesTags: ["WalletBalance"],
    }),
    //payPost
    getWithdrawalFees: builder.query<
      { name: string; percentage?: number; flatFeeUsdt?: number }[],
      void
    >({
      query: () => ({
        url: "/api/withdrawals/fees",
        method: "GET",
      }),
    }),

    buyPost: builder.mutation<
      {
        success: boolean;
        message?: string;
        status?: "pending" | "succeeded" | "failed";
        transactionId?: string;
      },
      { postId: string; amountSats: number }
    >({
      query: ({ postId, amountSats }) => ({
        url: `/wallet/${postId}/purchase`,
        method: "POST",
        body: {
          amount_usdt: amountSats,
        },
      }),
      invalidatesTags: ["WalletBalance"],
      async onQueryStarted({ postId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          const postData = await dispatch(
            postApi.endpoints.getPostByUid.initiate(postId, {
              forceRefetch: true,
            })
          ).unwrap();

          dispatch(updatePost(postData));
        } catch {}
      },
    }),
  }),
});

export const {
  useGetWalletBalanceQuery,
  useCheckEmailMutation,
  useLoginMutation,
  useLoginWithGoogleMutation,
  useLoginWithAppleMutation,
  useLogoutFromWalletMutation,
  useGetWalletsQuery,
  useSwitchWalletMutation,
  useDecodeQrMutation,
  useGetQrInvoiceMutation,
  useSendSatsMutation,
  useGetWalletStatusQuery,
  usePasswordResetRequestMutation,
  usePasswordResetResendMutation,
  useGetWalletHistoryQuery,
  useRegisterWithWalletMutation,
  useBuyWithUsdtMutation,
  usePasswordResetConfirmMutation,
  useZapMutation,
  useLikeMutation,
  usePurchaseFocusListMutation,
  useConvertCryptoQuery,
  useSwapMutation,
  useBuyPostMutation,
  useInitiateWithdrawalMutation,
  useResendWithdrawalOtpMutation,
  useConfirmWithdrawalMutation,
  useGetVerifiedAddressesQuery,
  useAddVerifiedAddressMutation,
  useDeleteVerifiedAddressMutation,
  useSendFundsMutation,
  useGetDepositLimitsQuery,
  useCreateDepositInvoiceMutation,
  useGetWithdrawalFeesQuery,
} = walletApi;
